/**
 * Run history — every run of THIS workflow, expanding into the same step
 * timeline the Events view renders (one read model, one component). Every
 * run pins the definition version it started on, so the timeline shows the
 * steps that run actually had — including the ones still ahead.
 */
import { Fragment } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Badge, Banner, Table, type BadgeVariant } from '@servicecompressionllc/sc-design-system';
import { AppShell } from '../../components/AppShell.js';
import { RunStepsTimeline } from '../events/EventRuns.js';
import { useBuilderRunsLogic } from './useBuilderLogic.js';

/** Engine words never reach the page — this is the whole translation layer. */
const RUN_STATUS: Record<string, { label: string; variant: BadgeVariant }> = {
  done: { label: 'Finished', variant: 'success' },
  suspended: { label: 'Waiting on someone', variant: 'warning' },
  deferred: { label: 'Paused — daily limit', variant: 'info' },
  failed: { label: 'Something went wrong', variant: 'danger' },
  running: { label: 'Running', variant: 'info' },
};
const runStatus = (s: string): { label: string; variant: BadgeVariant } =>
  RUN_STATUS[s] ?? { label: s, variant: 'info' };

export const BuilderRunsPage = () => {
  const { workflowId } = useParams();
  const { workflow, runs, isLoading, openRunId, toggleRun } = useBuilderRunsLogic(workflowId);

  if (!isLoading && !workflow) {
    return (
      <AppShell>
        <Banner variant="warning">Workflow not found.</Banner>
      </AppShell>
    );
  }

  const suspended = runs.filter((r) => r.status === 'suspended').length;
  const failed = runs.filter((r) => r.status === 'failed').length;

  return (
    <AppShell>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-xl font-semibold">{workflow?.name ?? '…'} — runs</h1>
          <p className="text-sm text-ink-muted">
            {runs.length} recent · {suspended} waiting on someone · {failed} with a problem ·{' '}
            <Link
              to={`/settings/workflows/${workflowId ?? ''}`}
              className="text-primary hover:underline"
            >
              back to editor
            </Link>
          </p>
        </div>
      </div>

      {runs.length === 0 && !isLoading ? (
        <Banner variant="info">
          No runs yet — this workflow hasn’t processed an event. Enable it and send a matching
          event, or check the unmatched counter on the Workflows list.
        </Banner>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-outline">
          <Table.Root className="w-full min-w-[48rem] text-sm">
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Started</Table.HeaderCell>
                <Table.HeaderCell>Event</Table.HeaderCell>
                <Table.HeaderCell>Status</Table.HeaderCell>
                <Table.HeaderCell>Steps</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {runs.map((run) => {
                const done = run.steps.filter((s) => s.status !== 'pending').length;
                return (
                  <Fragment key={run.id}>
                    <Table.Row
                      className="cursor-pointer"
                      onClick={() => {
                        toggleRun(run.id);
                      }}
                    >
                      <Table.Cell className="text-ink-muted">
                        {new Date(run.startedAt).toLocaleString()}
                      </Table.Cell>
                      <Table.Cell>
                        <span className="text-xs text-ink-muted">{run.eventType}</span>
                        {run.caseRef && (
                          <Link
                            className="mt-1 block text-xs text-primary hover:underline"
                            to={`/?case=${encodeURIComponent(run.caseRef)}`}
                            onClick={(event) => event.stopPropagation()}
                          >
                            {run.caseRef}
                          </Link>
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        <Badge variant={runStatus(run.status).variant}>
                          {runStatus(run.status).label}
                        </Badge>
                      </Table.Cell>
                      <Table.Cell className="text-ink-muted">
                        {run.status === 'suspended'
                          ? 'waiting for a decision'
                          : `step ${String(Math.min(done, run.steps.length))} of ${String(run.steps.length)}`}
                      </Table.Cell>
                    </Table.Row>
                    {openRunId === run.id && (
                      <Table.Row>
                        <Table.Cell colSpan={4} className="bg-surface">
                          <div className="py-1">
                            <RunStepsTimeline steps={run.steps} ariaLabel={`Run ${run.id} steps`} />
                          </div>
                        </Table.Cell>
                      </Table.Row>
                    )}
                  </Fragment>
                );
              })}
            </Table.Body>
          </Table.Root>
        </div>
      )}
    </AppShell>
  );
};
